// src/TTSButton.tsx
import React, { useState, useEffect } from 'react';
import { VolumeUp, VolumeOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface TTSButtonProps {
  text: string;
}

const TTSButton: React.FC<TTSButtonProps> = ({ text }) => {
  const { i18n } = useTranslation();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const utteranceRef = React.useRef<SpeechSynthesisUtterance | null>(null);

  const emitTtsState = (speaking: boolean) => {
    window.dispatchEvent(new CustomEvent('sarvalok:tts-state', { detail: { speaking } }));
  };

  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const v = window.speechSynthesis.getVoices();
        setVoices(v);
      };
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      emitTtsState(false);
    };
  }, []);

  const mapLocaleToBCP47 = (locale: string): string => {
    const base = (locale || 'en').toLowerCase();
    const short = base.includes('-') ? base.split('-')[0] : base;
    switch (short) {
      case 'cn': return 'zh-CN';
      case 'zh': return 'zh-CN';
      case 'en': return 'en-US';
      case 'de': return 'de-DE';
      case 'es': return 'es-ES';
      case 'pl': return 'pl-PL';
      case 'hi': return 'hi-IN';
      case 'ta': return 'ta-IN';
      case 'te': return 'te-IN';
      case 'ne': return 'ne-NP';
      case 'he': return 'he-IL';
      case 'fr': return 'fr-FR';
      case 'ja': return 'ja-JP';
      case 'pt': return 'pt-PT';
      case 'id': return 'id-ID';
      case 'ru': return 'ru-RU';
      case 'nl': return 'nl-NL';
      default: return 'en-US';
    }
  };

  const handleSpeak = () => {
    if ('speechSynthesis' in window) {
      // Stop any existing speech
      window.speechSynthesis.cancel();
      const plainText = text.replace(/<\/?[^>]+(>|$)/g, "");
      const utterance = new SpeechSynthesisUtterance(plainText);
      utteranceRef.current = utterance;

      // Set language/voice based on current locale
      const langTag = mapLocaleToBCP47(i18n.language || 'en');
      utterance.lang = langTag;
      const langPrefix = langTag.split('-')[0].toLowerCase();
      const selected = voices.find(v => v.lang && v.lang.toLowerCase().startsWith(langPrefix));
      if (selected) utterance.voice = selected;

      // Event listeners
      utterance.onstart = () => {
        setIsSpeaking(true);
        emitTtsState(true);
      };
      utterance.onend = () => {
        setIsSpeaking(false);
        emitTtsState(false);
      };
      utterance.onerror = () => {
        setIsSpeaking(false);
        emitTtsState(false);
      };

      window.speechSynthesis.speak(utterance);
    } else {
      alert('Text-to-Speech is not supported in your browser.');
    }
  };

  const handleStop = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      emitTtsState(false);
    }
  };

  return (
    <button
      title={isSpeaking ? 'Stop' : 'Listen'}
      onClick={isSpeaking ? handleStop : handleSpeak}
      className="p-1 text-gray-600 hover:text-gray-800 mr-1"
    >
      {isSpeaking ? <VolumeOff /> : <VolumeUp />}
    </button>
  );
};

export default TTSButton;
